import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Phone, Mail, MessageSquare, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPES = ['Appel', 'Email', 'Visite', 'Autre'];

const getTypeIcon = (type) => {
  const icons = {
    'Appel': <Phone className="w-4 h-4" />,
    'Email': <Mail className="w-4 h-4" />,
    'Visite': <MessageSquare className="w-4 h-4" />,
    'Autre': <Clock className="w-4 h-4" />
  };
  return icons[type];
};

const getTypeColor = (type) => {
  const colors = {
    'Appel': 'bg-blue-100 text-blue-800',
    'Email': 'bg-purple-100 text-purple-800',
    'Visite': 'bg-green-100 text-green-800',
    'Autre': 'bg-gray-100 text-gray-800'
  };
  return colors[type];
};

const Interactions = () => {
  const [interactions, setInteractions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    contact_id: '',
    type_interaction: 'Appel',
    notes: '',
    action: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [interResponse, contactsResponse] = await Promise.all([
        axios.get(`${API}/interactions`, { withCredentials: true }),
        axios.get(`${API}/contacts`, { withCredentials: true })
      ]);
      setInteractions(interResponse.data);
      setContacts(contactsResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.contact_id || !formData.notes) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/interactions`, formData, {
        withCredentials: true
      });
      toast.success('Interaction enregistrée avec succès');
      setDialogOpen(false);
      fetchData();
      setFormData({
        contact_id: '',
        type_interaction: 'Appel',
        notes: '',
        action: ''
      });
    } catch (error) {
      console.error('Error creating interaction:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002255]"></div>
      </div>
    );
  }

  return (
    <div data-testid="interactions-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#002255] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Interactions
          </h1>
          <p className="text-gray-600">Historique de vos échanges avec les contacts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white"
              data-testid="add-interaction-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Interaction
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enregistrer une Interaction</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="contact_id">Contact *</Label>
                <Select 
                  value={formData.contact_id} 
                  onValueChange={(value) => setFormData(prev => ({...prev, contact_id: value}))}
                >
                  <SelectTrigger data-testid="select-contact">
                    <SelectValue placeholder="Sélectionnez un contact" />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map(contact => (
                      <SelectItem key={contact.contact_id} value={contact.contact_id}>
                        {contact.nom} {contact.prenoms}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="type_interaction">Type d'interaction *</Label>
                <Select 
                  value={formData.type_interaction} 
                  onValueChange={(value) => setFormData(prev => ({...prev, type_interaction: value}))}
                >
                  <SelectTrigger data-testid="select-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes">Notes *</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                  placeholder="Décrivez l'interaction..."
                  rows={4}
                  required
                  data-testid="input-notes"
                />
              </div>

              <div>
                <Label htmlFor="action">Action de suivi</Label>
                <Input
                  id="action"
                  value={formData.action}
                  onChange={(e) => setFormData(prev => ({...prev, action: e.target.value}))}
                  placeholder="Rappeler dans 3 jours"
                  data-testid="input-action"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#002255] hover:bg-[#002255]/90"
                data-testid="submit-interaction-button"
              >
                Enregistrer l'interaction
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200/40 p-6">
        {interactions.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Aucune interaction enregistrée</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#002255] hover:bg-[#002255]/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Enregistrer votre première interaction
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {interactions.map(interaction => {
              const contact = contacts.find(c => c.contact_id === interaction.contact_id);
              return (
                <div
                  key={interaction.interaction_id}
                  className="flex gap-4 pb-6 border-b border-gray-100 last:border-0"
                  data-testid={`interaction-${interaction.interaction_id}`}
                >
                  <div className={`w-10 h-10 rounded-full ${getTypeColor(interaction.type_interaction)} flex items-center justify-center flex-shrink-0`}>
                    {getTypeIcon(interaction.type_interaction)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-[#002255]">
                          {contact ? `${contact.nom} ${contact.prenoms}` : 'Contact inconnu'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {format(new Date(interaction.date), 'PPPp', { locale: fr })}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(interaction.type_interaction)}`}>
                        {interaction.type_interaction}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{interaction.notes}</p>
                    {interaction.action && (
                      <div className="flex items-center gap-2 text-sm text-[#FF6600]">
                        <Clock className="w-4 h-4" />
                        <span>Action: {interaction.action}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Interactions;
